// THREAD ORDER — the authored counterpart of walk.js's spine (DECISIONS.md
// 2026-08-06 "A thread is a walk you authored").
//
// Named `threadOrder`, not `thread`, because deciding the reading order IS the
// whole job of this module — and because `Thread.js` is the surface next door
// and the two collapse into one file on a case-insensitive filesystem. Do not
// rename it back.
//
// Pure functions over the /api/threads/:id payload; no new data. Same split as
// the Walk: the SERVER bounds the closure (a recursive traversal under the
// viewer's visibility), the CLIENT decides the reading order. Sharing that split
// is not incidental — it is what makes a thread and a walk the same kind of
// object rendered by two rules, rather than two features that happen to look
// alike.
//
// THE ONE PLACE THIS DEPARTS FROM THE WALK, and the reason to state it loudly:
// extractSpine ranks each fork by the viewer's blended lens score, because it is
// answering "what is the strongest line of reasoning HERE, for YOU". A thread is
// answering something else — "what did the author put next" — so ranking by
// score would let a well-endorsed step the author never chose jump the queue and
// silently rewrite their sequence. The fork rule is therefore:
//
//   1. the THREAD AUTHOR'S own connectors first — a thread is authored, and its
//      author's stitches are what it IS. Someone else adding a `then` edge to
//      the same object is proposing a step, not editing the thread;
//   2. then `standing` (shared with the Walk): a step the community has REFUSED
//      is never walked, even when it is the only candidate;
//   3. then the order they were stitched (`created_at`), which is what stands in
//      for the position column this design deliberately does not have.
//
// `activated` and `standing` are imported rather than redefined, so the two
// traversals cannot disagree about what a rejected step is.
import { standing, activated } from './walk';

export { standing, activated };

// A branch is a fork the AUTHOR marked as one, not a fork the renderer guessed
// at. Every relation a thread follows other than its main one renders as a
// collapsible side line — which is what makes a branching thread readable, and
// why the composition vocabulary is two forward-pointing names instead of one.
export const isMainStep = (edge, mainRelation) => edge.relation_name === mainRelation;

// Rank the candidate connectors leaving one node. Exported for the unit tests,
// which drive it directly rather than through the whole traversal.
export function rankConnectors(edges, authorId) {
  return [...edges].sort(
    (a, b) =>
      Number(b.creator === authorId) - Number(a.creator === authorId) ||
      standing(b) - standing(a) ||
      String(a.created_at).localeCompare(String(b.created_at)) ||
      String(a.id).localeCompare(String(b.id))
  );
}

/**
 * Build the reading order: a nested array of steps.
 *
 *   [{ node, edge, depth, index, branches: [{ label, steps: [...] }], repeat }]
 *
 * `edge` connects this step to the PREVIOUS one and is null on the root — the
 * same step shape extractSpine returns, so a renderer written for one reads the
 * other. `branches` holds the side lines hanging off this step, each a nested
 * thread of its own.
 *
 * CYCLES ARE RENDERED, NOT PRUNED. A step already visited on the path above it
 * comes back with `repeat` set to that step's number and is not expanded
 * further. A circular thread is a finding — the reader should see the loop
 * close, with a pointer back to where it started, rather than be handed a
 * shorter linear thread that quietly omits it. Same rule the relation tree
 * already follows.
 */
export function extractThread(rootId, nodes, edges, opts = {}) {
  const {
    mainRelation = 'then',
    authorId = null,
    maxSteps = 200,
    maxBranchDepth = 4,
  } = opts;

  const byId = new Map(nodes.map((n) => [n.id, n]));
  // Every node rendered ANYWHERE in this traversal, for the duplicate marker
  // below. Distinct from `path`, which is one line's ancestry.
  const seen = new Set();
  const out = new Map(); // source id → connectors leaving it
  for (const e of edges) {
    // A refused connector is not a step. Checked here rather than at each fork
    // so a rejected edge cannot become "the" continuation merely by being the
    // only candidate — the Walk's rule, for the Walk's reason.
    if (standing(e) === 0) continue;
    if (!byId.has(e.source) || !byId.has(e.target)) continue;
    if (!out.has(e.source)) out.set(e.source, []);
    out.get(e.source).push(e);
  }

  let budget = maxSteps;

  // `path` is the ancestry of THIS line only, so a node reached twice by two
  // different branches renders twice (which is true — it is in both) while a
  // node that reaches itself renders once with the loop marked.
  const walk = (nodeId, edge, path, labels, depth, branchDepth) => {
    if (budget-- <= 0) return [];
    const node = byId.get(nodeId);
    if (!node) return [];

    const repeat = path.has(nodeId) ? labels.get(nodeId) ?? true : null;
    // DUPLICATE ≠ REPEAT, and keeping them apart is the whole point.
    //
    // `repeat` is a CYCLE: this line came back to a node above it, so expanding
    // would not terminate, and the step is not an editable position.
    // `duplicate` is the same object appearing in two different places — an
    // author hanging one claim under two headings, which is legal and often
    // deliberate. It still expands, it is still a position you can append after,
    // and the ONLY thing it needs is for the reader to be told, or a thread
    // reads as containing more distinct claims than it does.
    //
    // Overloading `repeat` for this would have made every duplicate uneditable
    // and moved `lastRealStep`, silently breaking append on any thread that
    // mentions a claim twice.
    const duplicate = !repeat && seen.has(nodeId);
    seen.add(nodeId);
    const step = { node, edge, depth, index: depth + 1, branches: [], repeat, duplicate };
    if (repeat) return [step];

    const nextPath = new Set(path).add(nodeId);
    const nextLabels = new Map(labels).set(nodeId, depth + 1);
    const candidates = rankConnectors(out.get(nodeId) || [], authorId);

    const main = candidates.filter((e) => isMainStep(e, mainRelation));
    const side = candidates.filter((e) => !isMainStep(e, mainRelation));

    if (branchDepth < maxBranchDepth) {
      for (const e of side) {
        const steps = walk(e.target, e, nextPath, nextLabels, 0, branchDepth + 1);
        if (steps.length) step.branches.push({ edge: e, label: e.relation_name, steps });
      }
    }

    // The FIRST main connector continues this line. Any others are additional
    // lines from the same point — rendered as branches, because a reader can
    // follow one sequence at a time and pretending otherwise is how a thread
    // stops being a thread. They keep their own relation name as the label, so
    // nothing about them is hidden, only nested.
    const [first, ...rest] = main;
    if (branchDepth < maxBranchDepth) {
      for (const e of rest) {
        const steps = walk(e.target, e, nextPath, nextLabels, 0, branchDepth + 1);
        if (steps.length) {
          step.branches.push({ edge: e, label: e.relation_name, steps, alsoMain: true });
        }
      }
    }
    if (!first) return [step];
    return [step, ...walk(first.target, first, nextPath, nextLabels, depth + 1, branchDepth)];
  };

  return walk(rootId, null, new Set(), new Map(), 0, 0);
}

// The visible MAIN line — what a reader is promised, branches excluded.
export function mainLine(steps) {
  return steps.map((s) => s.node);
}

/**
 * Index of the last REAL step — the one an append hangs off. −1 if there is
 * none.
 *
 * A cycle-closing repeat sits at the END of the list and is not a position: it
 * is a pointer back to where the chain rejoins itself. Anchoring an edit on one
 * writes an edge from a node to itself, which is what happened on the second
 * drive of the editor — "add a step at the end" appended the root to the root
 * and the thread collapsed to one step.
 *
 * Lives here rather than inline in the renderer because it is a fact about the
 * SHAPE the traversal returns, and every future edit affordance needs it.
 */
export function lastRealStep(steps) {
  for (let i = steps.length - 1; i >= 0; i--) {
    if (!steps[i].repeat) return i;
  }
  return -1;
}

/** A repeat marks where the chain loops; it is never an editable position. */
export const isEditableStep = (step) => !!step && !step.repeat;

/**
 * Group CONSECUTIVE sibling branches that share a label.
 *
 * The traversal makes one branch per side connector, which is structurally
 * right and reads badly: five `branches_to` edges applied from one heading in a
 * single call came back as five separate collapsed folds each holding one step,
 * so the reader saw "branches to" five times instead of a list of five things.
 *
 * CONSECUTIVE, not global. Adjacent siblings sharing a label were applied as a
 * set and read as one; siblings separated by a differently-labelled branch were
 * not, and merging across the gap would reorder the author's arrangement to make
 * the grouping tidier. That is the same mistake as ranking a thread by score.
 *
 * `alsoMain` is part of the key because those are extra MAIN lines nested as
 * branches — a different kind of thing from a side line, even under the same
 * name. It is normalized to a real boolean here: the traversal sets it to `true`
 * or leaves it undefined, and the two twins have to emit the same shape or the
 * parity assert fails on a difference nobody intended.
 *
 * ⚠ A LINE-FOR-LINE PORT of `groupBranches` in backend/lib/threadSpine.ts.
 */
export function groupBranches(branches) {
  const groups = [];
  for (const b of branches) {
    const last = groups[groups.length - 1];
    if (last && last.label === b.label && last.alsoMain === !!b.alsoMain) {
      last.lines.push(b.steps);
    } else {
      groups.push({ label: b.label, alsoMain: !!b.alsoMain, lines: [b.steps] });
    }
  }
  return groups;
}

// Every node a thread touches, branches and repeats included — what the publish
// audit is about, and what a "N steps" count must not quietly under-report.
export function allThreadNodes(steps, seen = new Set()) {
  for (const s of steps) {
    seen.add(s.node.id);
    for (const b of s.branches || []) allThreadNodes(b.steps, seen);
  }
  return seen;
}
